// src/functions/portalBillingApi.js
// Lane 5 (Phase 3b) — portal billing surfaces (Services + Settings saved-card
// management). Endpoint paths/shapes are frozen in docs/billing-contract.md
// section 2.2. Do not add fetchers here for other lanes' concerns.

import { apiAxios } from "./axiosInstance";

const unwrap = (error, fallback) => {
  if (error.response?.data) {
    throw new Error(error.response.data.detail || fallback);
  }
  throw new Error(error.message || fallback);
};

// GET /api/invoices/client/subscriptions
// Response: { items: SubscriptionProjection[] }
export const getClientSubscriptions = async () => {
  try {
    const response = await apiAxios.get("/invoices/client/subscriptions");
    return response.data?.items || [];
  } catch (error) {
    return unwrap(error, "Failed to fetch subscriptions");
  }
};

// POST /api/invoices/client/subscriptions/{id}/request-cancellation
// Records the client's request and alerts an operator. It does NOT cancel: an
// operator cancels from the dashboard after speaking to them.
export const requestSubscriptionCancellation = async (subscriptionId, reason) => {
  try {
    const response = await apiAxios.post(
      `/invoices/client/subscriptions/${subscriptionId}/request-cancellation`,
      { reason: reason || null }
    );
    return response.data;
  } catch (error) {
    return unwrap(error, "Failed to submit your cancellation request");
  }
};

// GET /api/invoices/client/payment-methods
// Response: { default_payment_method_id, payment_methods: PaymentMethodDisplay[] }
export const getClientPaymentMethods = async () => {
  try {
    const response = await apiAxios.get("/invoices/client/payment-methods");
    return {
      defaultPaymentMethodId: response.data?.default_payment_method_id || null,
      paymentMethods: response.data?.payment_methods || [],
    };
  } catch (error) {
    return unwrap(error, "Failed to fetch payment methods");
  }
};

// POST /api/invoices/client/payment-methods/setup-intent
// Response: { setupIntentSecret }
export const createPaymentMethodSetupIntent = async () => {
  try {
    const response = await apiAxios.post(
      "/invoices/client/payment-methods/setup-intent"
    );
    return response.data;
  } catch (error) {
    return unwrap(error, "Failed to start card setup");
  }
};

// POST /api/invoices/client/payment-methods/{pm_id}/default
// Response: { default_payment_method_id }
export const setDefaultPaymentMethod = async (pmId) => {
  try {
    const response = await apiAxios.post(
      `/invoices/client/payment-methods/${pmId}/default`
    );
    return response.data;
  } catch (error) {
    return unwrap(error, "Failed to set default payment method");
  }
};

// DELETE /api/invoices/client/payment-methods/{pm_id}
// Response: { removed, default_payment_method_id }
// Contract note: a pm_id backing an active subscription may 409; surface that
// message rather than assuming success.
export const removePaymentMethod = async (pmId) => {
  try {
    const response = await apiAxios.delete(
      `/invoices/client/payment-methods/${pmId}`
    );
    return response.data;
  } catch (error) {
    return unwrap(error, "Failed to remove payment method");
  }
};
